// Utilities for working with review star ratings
// Keep logic centralized so UI and data calculations stay consistent.

/**
 * Normalize any star rating input (text, number, numeric string) to a 0-5 number.
 * Accepts values like "FIVE", "five", "5", "5.0", "FIVE_STAR".
 */
export function normalizeStarRating(raw: string | number | null | undefined): number {
  if (raw === null || raw === undefined) return 0

  if (typeof raw === 'number' && !Number.isNaN(raw)) {
    return clampRating(raw)
  }

  const value = raw.toString().trim()
  if (!value) return 0

  // Numeric strings (e.g., "4", "4.5")
  const numeric = Number(value)
  if (!Number.isNaN(numeric)) {
    return clampRating(numeric)
  }

  // Text-based ratings (e.g., "FIVE", "FIVE_STAR")
  const normalized = value.toUpperCase().replace(/[^A-Z]/g, '')
  const ratingMap: Record<string, number> = {
    ONE: 1,
    TWO: 2,
    THREE: 3,
    FOUR: 4,
    FIVE: 5,
    ONESTAR: 1,
    TWOSTAR: 2,
    THREESTAR: 3,
    FOURSTAR: 4,
    FIVESTAR: 5
  }

  return ratingMap[normalized] ? clampRating(ratingMap[normalized]) : 0
}

/**
 * Return the visual fill state for 5 stars given a numeric rating.
 * Each item is "full", "half", or "empty".
 */
export function getStarFillStates(rating: number): Array<'full' | 'half' | 'empty'> {
  const safe = clampRating(rating)
  return Array.from({ length: 5 }, (_, index) => {
    const position = index + 1
    if (safe >= position) return 'full'
    if (safe >= position - 0.5) return 'half'
    return 'empty'
  })
}

/**
 * Format average rating for display. Returns "N/A" if no reviews or invalid number.
 */
export function formatAverageRating(value: number | null | undefined, totalReviews?: number): string {
  if (!Number.isFinite(value) || (totalReviews ?? 0) === 0 || (value ?? 0) <= 0) return 'N/A'
  return Number(value).toFixed(1)
}

function clampRating(value: number): number {
  return Math.max(0, Math.min(5, value))
}
